export abstract class DomainError extends Error {
  public readonly code: string
  public readonly timestamp: Date

  constructor(message: string, code: string) {
    super(message)
    this.name = this.constructor.name
    this.code = code
    this.timestamp = new Date()
    
    // Maintains proper stack trace for where our error was thrown
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor)
    }
  }

  abstract toJSON(): Record<string, unknown>
}

export class ValidationError extends DomainError {
  constructor(message: string, public readonly field?: string) {
    super(message, 'VALIDATION_ERROR')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      field: this.field,
      timestamp: this.timestamp,
    }
  }
}

export class NotFoundError extends DomainError {
  constructor(resource: string, id?: string) {
    const message = id ? `${resource} with id "${id}" not found` : `${resource} not found`
    super(message, 'NOT_FOUND')
    this.resource = resource
    this.resourceId = id
  }

  public readonly resource: string
  public readonly resourceId?: string

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      resource: this.resource,
      resourceId: this.resourceId,
      timestamp: this.timestamp,
    }
  }
}

export class UnauthorizedError extends DomainError {
  constructor(message = 'Unauthorized access') {
    super(message, 'UNAUTHORIZED')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      timestamp: this.timestamp,
    }
  }
}

export class ForbiddenError extends DomainError {
  constructor(message = 'Access forbidden') {
    super(message, 'FORBIDDEN')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      timestamp: this.timestamp,
    }
  }
}

export class NetworkError extends DomainError {
  constructor(message = 'Network error occurred', public readonly status?: number) {
    super(message, 'NETWORK_ERROR')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      status: this.status,
      timestamp: this.timestamp,
    }
  }
}

export class TimeoutError extends DomainError {
  constructor(message = 'Request timeout') {
    super(message, 'TIMEOUT')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      timestamp: this.timestamp,
    }
  }
}

export class BusinessRuleViolationError extends DomainError {
  constructor(message: string, public readonly rule: string) {
    super(message, 'BUSINESS_RULE_VIOLATION')
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      rule: this.rule,
      timestamp: this.timestamp,
    }
  }
}

// Error type guards
export const isValidationError = (error: unknown): error is ValidationError =>
  error instanceof ValidationError

export const isNotFoundError = (error: unknown): error is NotFoundError =>
  error instanceof NotFoundError

export const isUnauthorizedError = (error: unknown): error is UnauthorizedError =>
  error instanceof UnauthorizedError

export const isForbiddenError = (error: unknown): error is ForbiddenError =>
  error instanceof ForbiddenError

export const isNetworkError = (error: unknown): error is NetworkError =>
  error instanceof NetworkError

export const isTimeoutError = (error: unknown): error is TimeoutError =>
  error instanceof TimeoutError

export const isBusinessRuleViolationError = (error: unknown): error is BusinessRuleViolationError =>
  error instanceof BusinessRuleViolationError

export const isDomainError = (error: unknown): error is DomainError =>
  error instanceof DomainError